// src/components/Footer.jsx
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Divider,
  Link,
} from "@mui/material";
import { motion } from "framer-motion";

const Footer = () => {
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  // Framer Motion Variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  };

  return (
    <motion.footer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        background: "linear-gradient(135deg, #1e1e2f, #3f3f5f, #1e1e2f)",
        backgroundSize: "400% 400%",
        animation: "gradientAnimation 15s ease infinite",
        color: "#ffffff",
        paddingTop: 48,
        paddingBottom: 48,
      }}
    >
      <Container maxWidth="lg">
        {/* Language Switcher */}
        <motion.div variants={fadeInUp} initial="hidden" animate="visible">
          <Box display="flex" justifyContent="flex-end" mb={4}>
            <FormControl sx={{ minWidth: 140 }} size="small">
              <Select
                value={i18n.language}
                onChange={(e) => changeLanguage(e.target.value)}
                sx={{
                  bgcolor: "grey.800",
                  color: "common.white",
                  ".MuiSvgIcon-root": { color: "common.white" },
                }}
              >
                <MenuItem value="en">🇬🇧 English</MenuItem>
                <MenuItem value="si">🇱🇰 Sinhala</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </motion.div>

        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={3}>
            <motion.div variants={fadeInUp} initial="hidden" animate="visible">
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Android Orchestrator
              </Typography>
              <Typography variant="body2" color="grey.400">
                {t("footer.companyDescription")}
              </Typography>
            </motion.div>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} md={3}>
            <motion.div variants={fadeInUp} initial="hidden" animate="visible">
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t("footer.quickLinks")}
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {["dashboard", "containers", "images", "volumes","networks"].map((link) => (
                  <Link
                    component={RouterLink}
                    to={`/${link}`}
                    color="grey.400"
                    underline="hover"
                    key={link}
                    whileHover={{ color: "#6366f1", scale: 1.05 }}
                  >
                    {t(`footer.${link}`)}
                  </Link>
                ))}
              </Box>
            </motion.div>
          </Grid>

          {/* Resources */}
          <Grid item xs={12} md={3}>
            <motion.div variants={fadeInUp} initial="hidden" animate="visible">
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t("footer.resources")}
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {["documentation", "apiReference", "support", "faq"].map((res) => (
                  <Link
                    href="#"
                    color="grey.400"
                    underline="hover"
                    key={res}
                    whileHover={{ color: "#6366f1", scale: 1.05 }}
                  >
                    {t(`footer.${res}`)}
                  </Link>
                ))}
              </Box>
            </motion.div>
          </Grid>

          {/* Contact & Social */}
          <Grid item xs={12} md={3}>
            <motion.div variants={fadeInUp} initial="hidden" animate="visible">
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t("footer.connect")}
              </Typography>
              <Box display="flex" gap={2} mb={2}>
                {/* Add SVG social icons with hover animations */}
              </Box>
              <Typography variant="body2" color="grey.400">
                Email: unicornprofessionalbay@gmail.com
              </Typography>
              <Typography variant="body2" color="grey.400">
                Phone: +94-75-0788368
              </Typography>
            </motion.div>
          </Grid>
        </Grid>

        {/* Bottom Bar */}
        <Divider sx={{ bgcolor: "grey.700", my: 4 }} />
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body2" color="grey.400">
            © {new Date().getFullYear()} Android Orchestrator. {t("footer.rights")}
          </Typography>
          <Box display="flex" gap={3} mt={{ xs: 2, md: 0 }}>
            {["privacy", "terms", "cookies"].map((link) => (
              <Link
                component={RouterLink}
                to={`/${link}`}
                color="grey.400"
                underline="hover"
                key={link}
                whileHover={{ color: "#6366f1", scale: 1.05 }}
              >
                {t(`footer.${link}`)}
              </Link>
            ))}
          </Box>
        </Box>
      </Container>

      {/* Animated Gradient Keyframes */}
      <style>
        {`
          @keyframes gradientAnimation {
            0% {background-position: 0% 50%;}
            50% {background-position: 100% 50%;}
            100% {background-position: 0% 50%;}
          }
        `}
      </style>
    </motion.footer>
  );
};

export default Footer;
